import axios from 'axios';

/**
 * candidateAuthService.js
 * Comprehensive Candidate Authentication Service
 * Manages Candidate Registration, Password Login, Email OTP, Password Reset,
 * and Token/Session state across localStorage (Remember Me) and sessionStorage.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const TOKEN_KEY = 'candidate_token';
const USER_KEY = 'candidate_user';

class CandidateAuthService {
  /**
   * Get the active candidate authentication token
   */
  getToken() {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || null;
  }

  /**
   * Get the active candidate profile object
   */
  getCurrentCandidate() {
    try {
      const userStr = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      console.error('Error parsing candidate user from storage:', e);
      return null;
    }
  }

  /**
   * Check if candidate is currently authenticated
   */
  isAuthenticated() {
    const token = this.getToken();
    const candidate = this.getCurrentCandidate();
    return Boolean(token && candidate && candidate.id);
  }

  /**
   * Save candidate session (supports Remember Me)
   */
  setSession(token, user, rememberMe = true) {
    if (rememberMe) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
    } else {
      sessionStorage.setItem(TOKEN_KEY, token);
      sessionStorage.setItem(USER_KEY, JSON.stringify(user));
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }

    // Dispatch global events for UI components
    window.dispatchEvent(new CustomEvent('candidateAuthChange', { detail: { isAuthenticated: true, user } }));
    window.dispatchEvent(new Event('authChange'));
  }

  /**
   * Clear candidate session
   */
  clearSession() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);

    window.dispatchEvent(new CustomEvent('candidateAuthChange', { detail: { isAuthenticated: false, user: null } }));
    window.dispatchEvent(new Event('authChange'));
  }

  /**
   * Create Axios headers with Bearer token if available
   */
  getAuthHeaders() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Progressive Candidate Registration - Step 1: Request OTP
   * Sends 6-digit OTP code to email. No candidate account is created yet.
   */
  async requestRegistrationOtp({ full_name, email, phone = '' }) {
    try {
      const response = await axios.post(`${API_URL}/candidate-auth/register/request-otp`, {
        full_name,
        email,
        phone
      });
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Unable to send verification email. Please try again.';
      return { success: false, error: message };
    }
  }

  /**
   * Progressive Candidate Registration - Step 2: Verify Registration OTP
   * Verifies that the entered 6-digit OTP is correct and unexpired.
   */
  async verifyRegistrationOtp({ email, otp }) {
    try {
      const response = await axios.post(`${API_URL}/candidate-auth/verify-registration-otp`, {
        email,
        otp
      });
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Invalid verification code.';
      return { success: false, error: message };
    }
  }

  /**
   * Progressive Candidate Registration - Step 3: Set Password & Create Account
   * Verifies OTP, hashes password, creates candidate record, and returns JWT session.
   */
  async completeRegistration({ full_name, email, otp, password, phone = '', rememberMe = true }) {
    try {
      const response = await axios.post(`${API_URL}/candidate-auth/register/complete`, {
        full_name,
        email,
        otp,
        password,
        phone
      });

      const data = response.data?.data || response.data;
      if (data?.session?.access_token && data?.user) {
        this.setSession(data.session.access_token, data.user, rememberMe);
      }

      return { success: true, data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to complete registration.';
      return { success: false, error: message };
    }
  }

  /**
   * Backward-compatible candidate registration wrapper
   */
  async register({ full_name, email, password, phone = '' }) {
    return this.requestRegistrationOtp({ full_name, email, phone });
  }

  /**
   * Password Login
   */
  async loginWithPassword({ email, password, rememberMe = true }) {
    try {
      const response = await axios.post(`${API_URL}/candidate-auth/login`, {
        email,
        password
      });

      const data = response.data?.data || response.data;
      if (data?.session?.access_token && data?.user) {
        this.setSession(data.session.access_token, data.user, rememberMe);
      }

      return { success: true, data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Invalid email or password.';
      return { success: false, error: message };
    }
  }

  /**
   * Request OTP for Passwordless Login
   */
  async requestLoginOtp({ email }) {
    try {
      const response = await axios.post(`${API_URL}/candidate-auth/request-otp-login`, { email });
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to send login code.';
      return { success: false, error: message };
    }
  }

  /**
   * Verify OTP for Passwordless Login
   */
  async verifyLoginOtp({ email, otp, rememberMe = true }) {
    try {
      const response = await axios.post(`${API_URL}/candidate-auth/verify-otp-login`, {
        email,
        otp
      });

      const data = response.data?.data || response.data;
      if (data?.session?.access_token && data?.user) {
        this.setSession(data.session.access_token, data.user, rememberMe);
      }

      return { success: true, data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Invalid login code.';
      return { success: false, error: message };
    }
  }

  /**
   * Request Password Reset OTP
   */
  async forgotPassword({ email }) {
    try {
      const response = await axios.post(`${API_URL}/candidate-auth/forgot-password`, { email });
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to request password reset.';
      return { success: false, error: message };
    }
  }

  /**
   * Reset Password with OTP
   */
  async resetPassword({ email, otp, new_password }) {
    try {
      const response = await axios.post(`${API_URL}/candidate-auth/reset-password-otp`, {
        email,
        otp,
        new_password
      });
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Password reset failed.';
      return { success: false, error: message };
    }
  }

  /**
   * Resend OTP
   */
  async resendOtp({ email, purpose = 'registration' }) {
    try {
      const response = await axios.post(`${API_URL}/candidate-auth/resend-otp`, {
        email,
        purpose
      });
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to resend code.';
      return { success: false, error: message };
    }
  }

  /**
   * Get authenticated profile from server
   */
  async getProfile() {
    try {
      const headers = this.getAuthHeaders();
      const response = await axios.get(`${API_URL}/candidate-auth/me`, { headers });
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to load profile.';
      return { success: false, error: message };
    }
  }

  /**
   * Fetch active screenings and submitted applications for authenticated candidate
   */
  async getMyScreenings() {
    try {
      const headers = this.getAuthHeaders();
      const cand = this.getCurrentCandidate() || JSON.parse(localStorage.getItem('user') || '{}');
      const params = {};
      if (cand?.email) {
        params.email = cand.email;
      }
      if (cand?.id) {
        params.candidate_id = cand.id;
      }
      const response = await axios.get(`${API_URL}/candidate-auth/my-screenings`, { headers, params });
      return { success: true, data: response.data?.data || response.data || [] };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to fetch candidate screenings.';
      return { success: false, error: message, data: [] };
    }
  }

  /**
   * Candidate Logout
   */
  async logout() {
    try {
      const headers = this.getAuthHeaders();
      await axios.post(`${API_URL}/candidate-auth/logout`, {}, { headers });
    } catch (e) {
      // Swallowed - client session cleanup takes precedence
    } finally {
      this.clearSession();
    }
    return { success: true };
  }

  /**
   * Subscribe to candidate auth changes
   */
  onAuthStateChange(callback) {
    const handler = (e) => {
      callback(e.detail || { isAuthenticated: this.isAuthenticated(), user: this.getCurrentCandidate() });
    };

    window.addEventListener('candidateAuthChange', handler);
    return {
      unsubscribe: () => window.removeEventListener('candidateAuthChange', handler)
    };
  }
}

export const candidateAuthService = new CandidateAuthService();
export default candidateAuthService;
